import axios from "axios";
import Config from "../../../Utils/Config";
import { messageAuthor } from "./messageAuthor";

// Lookups for the ticket forms.
//
// Both the logged-in form and the anonymous login-page form used to hardcode their
// dropdowns. The category list was three options written into the JSX in two places,
// while SupportCategory actually holds four — "Feature Request" existed in the data and
// could never be picked, so nobody could file one and no report could show them.
//
// Read through the scaffold rather than the .NET API: it needs no bearer token, which
// matters because the anonymous form runs on the login page before anyone has signed in.

const DATA_API = Config.DATA_API;

// Support desk routes on the same API — hand-written endpoints that sit alongside the
// scaffolded CRUD (routes/support.js in azuresql.swancreate.com). They exist because
// replying has to write the message AND send the email, which needs server-side
// credentials. Slug-aware like everything else: /api/support/dev on a figdev build.
export const SUPPORT_API = `https://azuresql.swancreate.com/api/support/${Config.DATA_API_SLUG}`;

const MEDIA_UPLOAD = "https://media.swancreate.com/upload";
const SUPPORT_UPLOAD_FOLDER = "support-tickets";

/**
 * The two status values this app writes.
 *
 * The support route whitelists five — "created", "Close", "In Progress", "Waiting for
 * User", "Waiting for Fig" — and rejects anything else with a 400. Of those, "Close" is
 * the only closed value the data has ever used, and "Waiting for Fig" is the only open
 * one still in play (all 773 closed production tickets are "Close"; the 3 open ones are
 * "Waiting for Fig"). Reopening writes that rather than "created", which would claim the
 * ticket had never been touched.
 *
 * These were string literals in three places. They are one place now so that closing from
 * a list row and closing from inside a ticket cannot drift apart.
 */
export const TICKET_STATUS = {
  OPEN: "Waiting for Fig",
  CLOSED: "Close",
};

/** True for any status that means closed, compared the way the whole app compares it. */
export const isClosedStatus = (status) =>
  String(status || "").trim().toLowerCase().startsWith("clos");

// The .NET host's wwwroot is mirrored into blob storage and served publicly from
// media.swancreate.com/images/TheFigApp/wwwroot. Anything the database stores as a
// path relative to that wwwroot — district logos, certificates, uploaded files — can be
// read from blob instead of api.figk-12.com by prefixing it, which is one less reason
// to keep that server alive.
const FIGAPP_MEDIA_BASE = "https://media.swancreate.com/images/TheFigApp/wwwroot";

/** Resolve a wwwroot-relative path to its blob URL. Absolute URLs pass through. */
export const buildMediaUrl = (relativePath) => {
  const p = String(relativePath || "").trim();
  if (!p) return null;
  if (p.startsWith("http")) return p;
  return `${FIGAPP_MEDIA_BASE}${p.startsWith("/") ? p : `/${p}`}`;
};

/**
 * Upload a reply attachment to the shared media service.
 *
 * The old .NET endpoint took the file as multipart and stored it on the API host's
 * disk, which is one more reason that endpoint could not be replaced by plain CRUD.
 * Files now go to the same media service the Training Intake module uses, and the
 * message row stores the resulting URL.
 *
 * Returns {ok, url, fileName, fileSize, fileExtension} or {ok:false, error}.
 */
export const uploadTicketAttachment = async (file) => {
  if (!file) return { ok: true, url: null };
  try {
    const form = new FormData();
    form.append("file", file);
    form.append("folder", SUPPORT_UPLOAD_FOLDER);
    const resp = await axios.post(MEDIA_UPLOAD, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    // Prefer the permalink the service returns rather than hand-building a URL — the
    // intake module hand-builds one and only gets away with it because it renames every
    // file to a GUID first, so the server never has to de-collide the name.
    const url =
      resp.data?.permalink ||
      resp.data?.path ||
      `https://media.swancreate.com/images/${SUPPORT_UPLOAD_FOLDER}/${file.name}`;
    const dot = file.name.lastIndexOf(".");
    return {
      ok: true,
      url,
      fileName: file.name,
      fileSize: file.size,
      fileExtension: dot > -1 ? file.name.slice(dot + 1).toLowerCase() : "",
    };
  } catch (err) {
    return { ok: false, error: err.response?.data?.message || err.message };
  }
};

/**
 * Post a reply and email the other party.
 *
 * Returns the email outcome alongside the saved message, so the UI can tell the truth
 * when a reply is recorded but the notification did not go out — the failure mode Megan
 * described as "I don't know if the responses are going through."
 */
export const replyToTicket = async ({
  ticketId, description, userId, isSentByFigSupport, attachment,
}) => {
  try {
    const res = await axios.post(`${SUPPORT_API}/tickets/${ticketId}/reply`, {
      description,
      userId,
      isSentByFigSupport,
      ...(attachment?.url
        ? {
            filePath: attachment.url,
            fileName: attachment.fileName,
            fileSize: attachment.fileSize,
            fileExtension: attachment.fileExtension,
          }
        : {}),
    });
    return res.data;
  } catch (err) {
    return { success: false, message: err.response?.data?.message || err.message };
  }
};

/**
 * One ticket plus its conversation, read from the scaffold.
 *
 * Replaces GET /api/SupportTicket/GetSupportTicketById. This has to move together with
 * the list: the list now reads dev tickets on a figdev build, so a detail view still
 * pointed at the .NET API would be looking for those ids in production and find nothing.
 *
 * Returns the same shape the screen already renders, including the camelCase
 * supportTicketTransaction collection.
 */
export const getTicketDetail = async (ticketId) => {
  try {
    const [tRes, txRes, cats, dists] = await Promise.all([
      axios.get(`${DATA_API}/SupportTicketMst`),
      axios.get(`${DATA_API}/SupportTicketTransaction`),
      getSupportCategories(),
      getSupportDistricts(),
    ]);

    const tickets = Array.isArray(tRes.data) ? tRes.data : tRes.data?.value || [];
    const t = tickets.find(
      (x) => String(x.TicketId).toLowerCase() === String(ticketId).toLowerCase()
    );
    if (!t) return null;

    const txs = (Array.isArray(txRes.data) ? txRes.data : txRes.data?.value || [])
      .filter((x) => String(x.TicketId).toLowerCase() === String(ticketId).toLowerCase())
      .sort((a, b) => new Date(a.CreatedOn || 0) - new Date(b.CreatedOn || 0))
      .map((x) => ({
        ticketTransactionId: x.TicketTransactionId,
        description: x.Description,
        filePath: x.FilePath,
        fileName: x.FileName,
        isSentByFigSupport: x.IsSentByFigSupport === true || x.IsSentByFigSupport === 1,
        transactionCreatedOn: x.CreatedOn,
        // The real sender, not "whoever raised the ticket" — see messageAuthor.js.
        msgUserName: messageAuthor(x, t).name,
        msgUserEmail: messageAuthor(x, t).email,
        // Delivery outcome, written by the support route. This is what lets the thread
        // show whether a reply actually went out.
        emailStatus: x.EmailStatus || null,
        emailedTo: x.EmailedTo || null,
        emailError: x.EmailError || null,
      }));

    const catName = (cats || []).find((c) => c.id === Number(t.SupportCategoryId))?.name;
    const distName = (dists || []).find(
      (d) => String(d.id).toLowerCase() === String(t.SchoolDistrictId || "").toLowerCase()
    )?.name;

    return {
      ticketId: t.TicketId,
      ticketReferenceNumber: t.TicketReferenceNumber,
      subject: t.subject,
      status: t.Status,
      priority: t.Priority,
      email: t.Email,
      phone: t.Phone,
      userName: t.UserName,
      createdOn: t.CreatedOn,
      supportCategoryName: catName || "Uncategorised",
      schoolDistrictName: distName || "",
      supportTicketTransaction: txs,
    };
  } catch (err) {
    console.error("[Help] Could not load ticket:", err.message);
    return null;
  }
};

/**
 * Raise a ticket.
 *
 * This must go to the same API the list reads. It used to post to the .NET endpoint,
 * which is hardcoded to production — so a ticket raised on figdev was written to the
 * live database while the list read dev, and never appeared anywhere the submitter
 * could see it.
 */
export const createTicket = async (payload) => {
  try {
    const res = await axios.post(`${SUPPORT_API}/tickets`, payload);
    return res.data;
  } catch (err) {
    return { success: false, message: err.response?.data?.message || err.message };
  }
};

/** Close or reopen a ticket. */
export const setTicketStatus = async ({ ticketId, status, modifiedBy }) => {
  try {
    const res = await axios.post(`${SUPPORT_API}/tickets/${ticketId}/status`, { status, modifiedBy });
    return res.data;
  } catch (err) {
    return { success: false, message: err.response?.data?.message || err.message };
  }
};

/** Mark the thread read by FIG support. Only ever called for staff. */
export const markTicketRead = async (ticketId) => {
  try {
    await axios.post(`${SUPPORT_API}/tickets/${ticketId}/read`, {});
    return true;
  } catch (_) {
    return false;
  }
};

let _categories = null;
let _districts = null;

/**
 * Tickets for a viewer, read straight from the scaffold.
 *
 * Replaces GET /api/SupportTicket/GetSupportTicketStatus on the .NET API, which is
 * hardcoded to api.figk-12.com and not slug-aware — so the ticket screen always read
 * PRODUCTION tickets, even on figdev. Through the scaffold it follows the build's slug
 * like everything else.
 *
 * Support staff see every ticket; everyone else sees only their own. The .NET endpoint
 * applied that rule server-side, so it has to be applied here now — a client-side filter
 * is not a security boundary, but the scaffold exposes the table either way, so this
 * changes what the screen shows rather than what is reachable.
 */
export const getTicketsForViewer = async ({ userId, isStaff }) => {
  try {
    const [ticketRes, txRes, catRes, distRes] = await Promise.all([
      axios.get(`${DATA_API}/SupportTicketMst`),
      axios.get(`${DATA_API}/SupportTicketTransaction`),
      getSupportCategories(),
      getSupportDistricts(),
    ]);

    const rows = Array.isArray(ticketRes.data) ? ticketRes.data : ticketRes.data?.value || [];
    const txs = Array.isArray(txRes.data) ? txRes.data : txRes.data?.value || [];
    const catById = new Map((catRes || []).map((c) => [c.id, c.name]));
    const distById = new Map((distRes || []).map((d) => [String(d.id).toLowerCase(), d]));

    // IsReadByAdmin is a column on SupportTicketTransaction, NOT on the ticket — the
    // .NET list endpoint flattened it, so reading it off the ticket row here would come
    // back undefined and mark every ticket unread. Derive it from the newest message.
    const newestTx = new Map();
    for (const tx of txs) {
      const key = String(tx.TicketId || "").toLowerCase();
      if (!key) continue;
      const cur = newestTx.get(key);
      if (!cur || new Date(tx.CreatedOn || 0) > new Date(cur.CreatedOn || 0)) newestTx.set(key, tx);
    }

    const mine = (t) =>
      String(t.CreatedBy || "").toLowerCase() === String(userId || "").toLowerCase() ||
      String(t.UserId || "").toLowerCase() === String(userId || "").toLowerCase();

    return rows
      .filter((t) => t.IsActive !== false)
      .filter((t) => isStaff || mine(t))
      // The scaffold returns the raw columns; map to the shape the screen already uses
      // so the .NET payload and this stay interchangeable. Note `subject` really is
      // lowercase on this table while its neighbours are PascalCase.
      .map((t) => ({
        ticketId: t.TicketId,
        refId: t.TicketReferenceNumber,
        subject: t.subject,
        status: t.Status,
        priority: t.Priority,
        category: catById.get(Number(t.SupportCategoryId)) || "Uncategorised",
        schoolDistrictName: distById.get(String(t.SchoolDistrictId || "").toLowerCase())?.name || "",
        districtLogo: distById.get(String(t.SchoolDistrictId || "").toLowerCase())?.logo || null,
        userName: t.UserName,
        email: t.Email,
        phone: t.Phone,
        createdOn: t.CreatedOn,
        // The status route stamps LastUpdatedOn when a ticket is closed, so on a closed
        // ticket this is effectively the close time — which is what the "closed in the
        // last 30 days" and "time to close" stats are measured from. It is not a close
        // time on an OPEN ticket, so only read it as one when the ticket is closed.
        lastUpdatedOn: t.LastUpdatedOn,
        isReadByAdmin: (() => {
          const tx = newestTx.get(String(t.TicketId || "").toLowerCase());
          // No messages yet means nobody has answered — treat as unread for staff.
          if (!tx) return false;
          return tx.IsReadByAdmin === true || tx.IsReadByAdmin === 1;
        })(),
        // Who spoke last. When the customer did, the ticket is waiting on FIG — that is
        // the thing Megan needs to spot without opening anything, and nothing in the old
        // screen showed it.
        awaitingReply: (() => {
          const tx = newestTx.get(String(t.TicketId || "").toLowerCase());
          if (!tx) return true;   // raised, never answered
          return !(tx.IsSentByFigSupport === true || tx.IsSentByFigSupport === 1);
        })(),
        lastMessageOn: newestTx.get(String(t.TicketId || "").toLowerCase())?.CreatedOn || t.CreatedOn,
        messageCount: (() => {
          const key = String(t.TicketId || "").toLowerCase();
          return txs.filter((x) => String(x.TicketId || "").toLowerCase() === key).length;
        })(),
        isWithOutLogin: t.IsWithOutLogin,
      }));
  } catch (err) {
    console.error("[Help] Could not load tickets:", err.message);
    return null; // null = failed, [] = genuinely none
  }
};

/** SupportCategory rows, ordered by id so the list is stable across renders. */
export const getSupportCategories = async () => {
  if (_categories) return _categories;
  try {
    const res = await axios.get(`${DATA_API}/SupportCategory`);
    const rows = Array.isArray(res.data) ? res.data : res.data?.value || [];
    _categories = rows
      .filter((r) => r.IsActive !== false)
      .map((r) => ({
        id: Number(r.SupportCategoryId),
        name: r.SupportCategoryName,
      }))
      .filter((r) => r.id && r.name)
      .sort((a, b) => a.id - b.id);
    return _categories;
  } catch (err) {
    console.error("[Help] Could not load support categories:", err.message);
    // Deliberately empty rather than a hardcoded guess — a stale hardcoded list is what
    // hid "Feature Request" in the first place. The form shows a plain message instead.
    return [];
  }
};

/**
 * Districts for the anonymous ticket form, as {id, name}.
 *
 * The old form was a free-text box, and the backend resolved it with
 * DistrictName.Contains(...), so a typo recorded no district and a partial name could
 * match the wrong one. Picking from the real list means the ticket carries the actual
 * SchoolDistrictId.
 */
export const getSupportDistricts = async () => {
  if (_districts) return _districts;
  try {
    const res = await axios.get(`${DATA_API}/SchoolDistrict`);
    const rows = Array.isArray(res.data) ? res.data : res.data?.value || [];
    _districts = rows
      // Deliberately NOT filtered on IsActive: 105 of 237 districts are inactive in
      // this data, and their tickets still need to show a name. Filtering them out left
      // 101 tickets that HAVE a district id reading "No district on file".
      .filter((r) => r.DistrictName)
      .map((r) => ({
        id: r.SchoolDistrictId,
        name: String(r.DistrictName).trim(),
        // Served from blob, not the .NET host. The api.figk-12.com wwwroot is mirrored
        // into blob storage under TheFigApp/wwwroot and exposed at
        // media.swancreate.com/images/ — so the relative path already on the row just
        // needs that prefix. Verified 12/12 sample districts return 200.
        //
        // NOT SchoolDistrict.DistrictLogoUrl: that column points at
        // media.swancreate.com/images/DistrictLogo/... (no TheFigApp/wwwroot segment)
        // and every value 404s. It is stale and should be ignored or backfilled.
        //
        // This removes the last api.figk-12.com dependency from this screen.
        logo: buildMediaUrl(r.ThumbnailLogoURL || r.DistrictLogo),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return _districts;
  } catch (err) {
    console.error("[Help] Could not load districts:", err.message);
    return [];
  }
};
